import type { Translate } from "@/lib/i18n/translate";
import type { LucideIcon } from "lucide-react";
import {
  AudioLines,
  BellRing,
  BookOpenText,
  Building2,
  Calculator,
  CalendarClock,
  CalendarDays,
  Clapperboard,
  ClipboardList,
  FileSearch,
  FileSpreadsheet,
  FolderOpen,
  Gauge,
  Gavel,
  GraduationCap,
  Inbox,
  KeyRound,
  Landmark,
  LifeBuoy,
  ListTodo,
  Mails,
  MessageCircle,
  MessagesSquare,
  MonitorSmartphone,
  Phone,
  PhoneOutgoing,
  PiggyBank,
  ReceiptEuro,
  ReceiptText,
  ScrollText,
  Search,
  ShieldCheck,
  Signature,
  Sparkles,
  Timer,
  TrendingUp,
  Triangle,
  Users,
  Wallet,
  Wrench,
} from "lucide-react";

export type ProductMenuItem = {
  readonly label: string;
  readonly href: string;
  readonly Icon: LucideIcon;
};

export type ProductMenuColumn = {
  readonly title: string;
  /** Grid columns taken; a 2 lays its items out in two sub-columns. */
  readonly span: 1 | 2;
  readonly items: readonly ProductMenuItem[];
};

/** Feature page path. Icons match the app's section icons where one exists. */
function featureHref(slug: string): string {
  return `/features/${slug}`;
}

function item(label: string, slug: string, Icon: LucideIcon): ProductMenuItem {
  return { label, href: featureHref(slug), Icon };
}

/** Produkt mega-menu: two rows of four grid columns; Buchhaltung spans two. */
export function productMenuColumns(t: Translate): readonly ProductMenuColumn[] {
  return [
    {
      title: t("kommunikation"),
      span: 1,
      items: [
        item(t("inbox"), "inbox", Inbox),
        item(t("telefonie"), "phone", Phone),
        item(t("serienbriefe"), "mailings", Mails),
        item(t("teamchat"), "team-chat", MessagesSquare),
      ],
    },
    {
      title: t("dokumente"),
      span: 1,
      items: [
        item(t("dokumente"), "documents", FolderOpen),
        item(t("onlineSignatur"), "e-signature", Signature),
        item(t("revisionssicherheit"), "audit-proof-archive", ShieldCheck),
      ],
    },
    {
      // Finance module (Zusatzmodul): pages come with the module, until then coming-soon.
      title: t("buchhaltung"),
      span: 2,
      items: [
        item(t("rechnungsFreigabe"), "invoice-approval", ReceiptText),
        item(t("bankanbindung"), "bank-connection", Landmark),
        item(t("hausgeldMieten"), "rent-collection", Wallet),
        item(t("mahnwesen"), "dunning", BellRing),
        item(t("wirtschaftsplan"), "budget-plan", PiggyBank),
        item(t("jahresabrechnung"), "annual-statement", FileSpreadsheet),
        item(t("betriebskostenabrechnung"), "service-charges", Calculator),
        item(t("mieterhoehungen"), "rent-increases", TrendingUp),
      ],
    },
    {
      title: t("stammdaten"),
      span: 1,
      items: [
        item(t("liegenschaften"), "properties", Building2),
        item(t("kontakte"), "contacts", Users),
        item(t("vertraege"), "leases", ScrollText),
        item(t("zaehler"), "meters", Gauge),
        item(t("schluessel"), "keys", KeyRound),
        item(t("instandhaltung"), "maintenance", Wrench),
      ],
    },
    {
      title: t("eigentuemerMieter"),
      span: 1,
      items: [
        item(t("kundenportal"), "customer-portal", MonitorSmartphone),
        item(t("eigentuemerversammlung"), "owners-meetings", Gavel),
        item(t("formulare"), "forms", ClipboardList),
        item(t("terminbuchung"), "booking", CalendarClock),
        item(t("hilfeCenter"), "help-center", LifeBuoy),
      ],
    },
    {
      title: t("organisation"),
      span: 1,
      items: [
        item(t("aufgabenBoards"), "tasks", ListTodo),
        item(t("kalender"), "calendar", CalendarDays),
        item(t("zeiterfassung"), "time-tracking", Timer),
        item(t("wissen"), "knowledge", BookOpenText),
        item(t("universalsuche"), "search", Search),
      ],
    },
    {
      title: t("mehr"),
      span: 1,
      items: [
        item(t("clips"), "clips", Clapperboard),
        item(t("lernbereich"), "learning", GraduationCap),
      ],
    },
  ];
}

/** Main pitch of the „KI“ menu: your own AI runs the whole software. */
export const AI_MENU_FEATURE_HREF: string = featureHref("api");

export const AI_MENU_FEATURE_LOGOS = [
  { name: "Claude", src: "/marketing/logos/claude.svg", mono: false }, // i18n-exempt: product name
  { name: "ChatGPT", src: "/marketing/logos/openai.svg", mono: true }, // i18n-exempt: product name
  { name: "Langdock", src: "/marketing/logos/langdock.png", mono: false }, // i18n-exempt: product name
] as const;

/** „KI“ menu next to Produkt: agents inside the software, beside the big own-AI card. */
export function aiMenuColumns(t: Translate): readonly ProductMenuColumn[] {
  return [
    {
      title: t("deineKi"),
      span: 1,
      items: [
        item(t("claudeUndCo"), "api", Sparkles),
      ],
    },
    {
      title: t("agentenInDerSoftware"),
      span: 1,
      items: [
        item(t("alleKiAgenten"), "ai-agents", Triangle),
        item(t("kundenChat"), "customer-chat", MessageCircle),
        item(t("kiAnrufbeantworter"), "ai-answering-service", AudioLines),
        item(t("kiAnrufe"), "ai-calls", PhoneOutgoing),
        item(t("dokumentenAgent"), "document-agent", FileSearch),
        item(t("rechnungsAgent"), "invoice-agent", ReceiptEuro),
      ],
    },
  ];
}

export type NavMenuKind = "product" | "ai";

export function navMenuColumns(
  kind: NavMenuKind,
  t: Translate,
): readonly ProductMenuColumn[] {
  return kind === "ai" ? aiMenuColumns(t) : productMenuColumns(t);
}
